import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { StandWithUkraineBadge } from "./footer/StandWithUkraineBadge";
import { filterNavItems, navItems, socials, type SubLink } from "./nav-data";
import { ConsentTrigger } from "./consent/ConsentTrigger";
import { Button } from "@/components/ui/button";

import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { FEATURE_FLAGS } from "@/lib/feature-flags.keys";

type Column = { label: string; basePath: string; children: SubLink[] };

function buildColumns(enabledFlags: readonly string[]): Column[] {
  return filterNavItems(navItems, enabledFlags)
    .filter((i): i is Extract<typeof navItems[number], { type: "group" }> => i.type === "group")
    .map((g) => ({ label: g.label, basePath: g.basePath, children: g.children }));
}

export function Footer() {
  const year = new Date().getFullYear();
  const [openColumns, setOpenColumns] = useState<Record<string, boolean>>({});
  const cockpitVisible = useFeatureFlag(FEATURE_FLAGS.cockpitPublicVisible);
  // Ticket ME1/KN1: verborgene Bereiche erscheinen in keinem Menü.
  const mentoringsVisible = useFeatureFlag(FEATURE_FLAGS.mentoringsVisible);
  const keynotesVisible = useFeatureFlag(FEATURE_FLAGS.keynotesVisible);
  const columns = buildColumns([
    ...(mentoringsVisible.enabled ? [FEATURE_FLAGS.mentoringsVisible] : []),
    ...(keynotesVisible.enabled ? [FEATURE_FLAGS.keynotesVisible] : []),
  ]);

  const toggle = (key: string) =>
    setOpenColumns((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="mx-auto grid max-w-[1400px] gap-12 px-6 py-20 md:grid-cols-4 md:px-12 md:py-24">
        <div>
          <div className="flex items-center gap-4 md:block">
            <Logo className="h-7 shrink-0" variant="dark" />
            <p className="min-w-0 font-display text-base font-extrabold leading-tight text-brand-button md:mt-8 md:max-w-xs md:text-2xl whitespace-pre-line">
              Kreativität und Strategie{"\n"}im Dienst der Demokratie.
            </p>
          </div>
          <nav aria-label="Soziale Netzwerke" className="mt-8 flex gap-3">
            {socials.map(({ label, href, icon: Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={label}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-primary-foreground/25 text-primary-foreground transition-colors hover:border-brand-button hover:text-brand-button"
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
              </a>
            ))}
          </nav>
          {cockpitVisible.enabled && (
            <div className="mt-10 border-t border-primary-foreground/20 pt-8">
              <Button
                asChild
                size="lg"
                className="w-full font-display font-extrabold focus-visible:ring-2 focus-visible:ring-brand-button focus-visible:ring-offset-2 focus-visible:ring-offset-primary sm:w-auto"
              >
                <Link to="/cockpit/login">Login</Link>
              </Button>
            </div>
          )}
        </div>



        {columns.map((col) => {
          const isOpen = !!openColumns[col.basePath];
          const listId = `footer-col-${col.basePath.replace(/\//g, "-")}`;
          return (
            <nav key={col.basePath} aria-label={col.label} className="hidden flex-col gap-4 md:flex">
              <button
                type="button"
                onClick={() => toggle(col.basePath)}
                aria-expanded={isOpen}
                aria-controls={listId}
                className="flex items-center justify-between gap-3 text-left font-display text-lg font-extrabold text-brand-button md:pointer-events-none md:cursor-default"
              >
                <span>{col.label}</span>
                <ChevronDown
                  className={`h-5 w-5 transition-transform md:hidden ${isOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </button>
              <ul
                id={listId}
                className={`flex-col gap-3 ${isOpen ? "flex" : "hidden"} md:flex`}
              >
                {col.children.map((child) => (
                  <li key={child.to}>
                    <Link
                      to={child.to}
                      className="text-sm text-primary-foreground/85 transition-colors hover:text-brand-button"
                    >
                      {child.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          );
        })}
      </div>

      <div className="border-t border-primary-foreground/20">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-4 px-6 py-6 pr-16 text-sm text-primary-foreground/80 md:flex-row md:items-center md:px-12 md:pr-12">
          <p className="text-[14px]">© {year} creategy GmbH&nbsp;</p>
          <nav aria-label="Rechtliches" className="flex flex-wrap items-center gap-x-6 gap-y-3 md:gap-10">
            <StandWithUkraineBadge />
            <Link to="/datenschutz" className="transition-colors hover:text-brand-button">
              Datenschutz
            </Link>
            <Link to="/impressum" className="transition-colors hover:text-brand-button">
              Impressum
            </Link>
            <Link to="/seitenuebersicht" className="transition-colors hover:text-brand-button">
              Seitenübersicht
            </Link>
            <ConsentTrigger />
          </nav>
        </div>
      </div>
    </footer>
  );
}
